import { Asset } from "@/components/graphql/schema.graphql";
import { FilepickerFile } from "@/components/ui-v2/FilePicker";
import { getExtensionsFromMimeType } from "@/utils/mimetype";

import { useFileUpload } from "./useFileUpload";

type UseConvertAssetSnapshotToFileParams = {
  onError?: (error: Error) => void;
  onSuccess?: (file: FilepickerFile) => void;
};

/**
 * Converts an asset (as a snapshot) to a file, so it can be used as a reference for inferences.
 * @param params Parameters for the hook
 * @returns A function that converts an asset snapshot to a file
 */
export const useConvertAssetSnapshotToFile = (
  params: UseConvertAssetSnapshotToFileParams,
) => {
  const { upload } = useFileUpload();

  const convertAssetSnapshotToFile = async (
    asset: Pick<Asset, "name" | "previewUrl">,
  ) => {
    try {
      if (!asset.previewUrl) {
        throw new Error("Asset has no preview URL");
      }
      const response = await fetch(asset.previewUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch asset: ${response.statusText}`);
      }

      const content = await response.blob();

      const extensions = getExtensionsFromMimeType(content.type);
      const file = new File(
        [content],
        asset.name || `refimg_${Date.now()}${extensions[0]}`,
        {
          type: content.type,
        },
      );

      const uploadedFile = await upload(file, {
        sourceType: "REFERENCE",
      });

      params.onSuccess?.(uploadedFile);
      return uploadedFile;
    } catch (error) {
      console.error("Error converting asset snapshot to file:", error);
      params.onError?.(error as Error);
    }
  };

  return { convertAssetSnapshotToFile };
};
